import { type MouseEvent, useCallback } from 'react';

import { type AnnouncementAction, isAnnouncementCommandAction } from '../constants';
import { isExternalAnnouncementHref } from '../utils/getAnnouncementLinkHref';

type AnnouncementAnchorTargetProps = { rel?: string; target?: string };

const isModifiedClick = (event: MouseEvent<HTMLElement>) => {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
};

// Native opens must match the host's new-tab handling of external urls; in-app paths stay in the same tab.
export const getAnnouncementAnchorTargetProps = (url: string): AnnouncementAnchorTargetProps => {
  return isExternalAnnouncementHref(url) ? { rel: 'noopener noreferrer', target: '_blank' } : {};
};

export const useAnnouncementActionClick = (
  action: AnnouncementAction,
  onAction: (action: AnnouncementAction) => void,
) => {
  return useCallback(
    (event: MouseEvent<HTMLElement>) => {
      // Let the browser handle modified clicks natively (new tab, copy link) instead of routing through the host.
      if (!isAnnouncementCommandAction(action) && isModifiedClick(event)) {
        return;
      }

      event.preventDefault();
      onAction(action);
    },
    [action, onAction],
  );
};
